/**
 * SelectCurrentVersion
 *
 * Dropdown in the version-select footer that promotes a form version to the
 * form's current version, behind a confirmation modal. Consumer:
 * `FormVersionSelect`.
 *
 * Reads three leaves off `form.data` (`docId`, `currentVersionId`, `formNumber`)
 * and walks `formVersions.docIds` reading only `formVersionNumber` per entry —
 * never spreading an entry, because that collection listener covers the open
 * version too. `useFormStore()` is banned here: it materializes the whole version
 * collection and spreads `formVersion.data`, so this footer re-rendered on every
 * keystroke in the form (SYD-2252).
 */

import { FC, useContext, useState } from 'react';
import { Modal, Typography, Dropdown, Button } from 'antd';
import { useSnapshot } from 'valtio';
import { formStore } from '../../../store';
import { setAsCurrentVersion } from '../../../services/artifactFunctions';
import { DownOutlined } from '@ant-design/icons';
import { FormPropsUIDescription, formVersionNumberPrefix } from '#types/System.Parameters.types';
import FlexCol from '../../atoms/FlexCol';
import { MessageContext } from '../../../providers/MessageContext';

/**
 * Interface defining the props for the SelectCurrentVersion component
 * 
 * @interface SelectCurrentVersionProps
 * @property {boolean} [disabled] - Whether the dropdown should be disabled
 * @property {boolean} [divider] - Whether to show a divider above the dropdown
 */
interface SelectCurrentVersionProps {
  disabled?: boolean;
}

const { Text } = Typography;

/**
 * Component that provides a dropdown interface for setting a form version as the current version
 * 
 * This component:
 * - Displays a dropdown with all available form versions
 * - Provides visual indication of which version is currently set as the current version
 * - Shows a confirmation modal before changing the current version
 * - Makes the API call to update the current version when confirmed
 * - Handles loading states and error messages
 * 
 * @component
 * @param {SelectCurrentVersionProps} props - Component props
 * @returns {JSX.Element} The rendered component
 */
const SelectCurrentVersion: FC<SelectCurrentVersionProps> = ({
  disabled = false
}) => {
  const snap = useSnapshot(formStore.state);
  const formDocId = snap.form.data?.docId;
  const currentVersionId = snap.form.data?.currentVersionId;
  const formNumber = snap.form.data?.formNumber;
  const versions = snap.formVersions.docIds.flatMap((id) => {
    const entry = snap.formVersions.docs.get(id);
    return entry ? [{ docId: id, formVersionNumber: entry.data?.formVersionNumber }] : [];
  });
  const { messageApi } = useContext(MessageContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [versionToSetAsCurrent, setVersionToSetAsCurrent] = useState<string | null>(null);

  /**
   * Handles the selection of a version from the dropdown
   * Opens the confirmation modal and stores the selected version ID
   * 
   * @param {string} versionId - The ID of the selected version
   * @param {string} versionNumber - The version number of the selected version (for display)
   */
  const handleVersionSelect = (versionId: string) => {
    setVersionToSetAsCurrent(versionId);
    setIsModalOpen(true);
  };

  /**
   * Handles the confirmation of setting a new current version
   * Makes the API call to update the current version and shows success/error messages
   * 
   * @async
   * @returns {Promise<void>}
   */
  const handleConfirm = async () => {
    if (!formDocId || !versionToSetAsCurrent) {
      messageApi.error('Form or version information is missing');
      return;
    }

    setIsLoading(true);
    try {
      await setAsCurrentVersion(
        formDocId,
        versionToSetAsCurrent
      );
      // Provide immediate UI feedback - backend notification will provide detailed transition info
      messageApi.success('Current version updated');
    } catch (error) {
      messageApi.error(`Failed to set current version: ${error instanceof Error ? error.message : String(error)}`);
      console.error('Error setting current version:', error);
    } finally {
      setIsModalOpen(false);
      setIsLoading(false);
      setVersionToSetAsCurrent(null);
    }
  };

  /**
   * Handles cancellation of the confirmation modal
   * Closes the modal and resets the selected version
   */
  const handleCancel = () => {
    setIsModalOpen(false);
    setVersionToSetAsCurrent(null);
  };

  const label = FormPropsUIDescription.formVersionNumber.shortLabel;

  // Create dropdown menu items for each form version
  const items = versions.map((version) => {
    const isCurrentVersion = currentVersionId === version.docId;

    return {
      key: version.docId,
      label: (
        <div onClick={() => handleVersionSelect(version.docId)}>
          {formVersionNumberPrefix}{version.formVersionNumber}
          <Text 
            type='success' 
            style={{ 
              color: isCurrentVersion ? '' : 'transparent' 
            }}
          >
            &nbsp;current
          </Text>
        </div>
      ),
      disabled: disabled || isCurrentVersion,
    };
  });

  const selectedVersionNumber = versionToSetAsCurrent
    ? versions.find(v => v.docId === versionToSetAsCurrent)?.formVersionNumber
    : '';

  return (
    <FlexCol style={{ width: '100%' }}>
      <Dropdown 
        menu={{ items }} 
        disabled={disabled || versions.length === 0}
      >
        <Button
          type='text'
          size='small'
          style={{ justifyContent: 'flex-start' }}
        >
          Set Current {label}
          <DownOutlined />
        </Button>
      </Dropdown>

      <Modal
        title="Set Current Version"
        open={isModalOpen}
        onOk={handleConfirm}
        onCancel={handleCancel}
        confirmLoading={isLoading}
        okText="Yes"
        cancelText="No"
      >
        <Text>
          Are you sure you want to change the current {label}?<br />
          <br />
          Form<br />
          <b>{formNumber}</b><br />
          New Current<br />
          <b>{selectedVersionNumber ? formVersionNumberPrefix + selectedVersionNumber : 'Selected ' + label}</b>
        </Text>
      </Modal>
    </FlexCol>
  );
};

export default SelectCurrentVersion; 